import Vue from 'vue';
import Vuex from 'vuex';
import VuexPersist from 'vuex-persist';
import { service } from './service';
import constants from '../constants/generic.js';
import opmGeneric from '../utils/generic.js';

Vue.use(Vuex);

const getDefaultState = () => ({
  loanEnquiryConfig: {},
  loanEnquiryData: {},
  elsConfig: {
    lstLabel: {},
    lstInUse: {},
    lstRequired: {},
    lstHelperText: {},
  },
  paymentConfirmed: false,
});

const state = getDefaultState();

const mutations = {
  loanEnquiryConfiguration(state, config) {
    
    if (config?.Response?.StatusCode === 0) {
      state.loanEnquiryConfig = config.LoanEnquiryConfig;
      // Parse field configuration for "Child.Components"
      if (config.LoanEnquiryConfig.FieldsConfiguration) {
        const isPrimaryLang = opmGeneric.isPrimaryLang();
        config.LoanEnquiryConfig.FieldsConfiguration.forEach(el => {
          state.elsConfig.lstInUse[el.Name] = true; // Only InUse fields are returned
          state.elsConfig.lstRequired[el.Name] = el.Required;
          state.elsConfig.lstLabel[el.Name] = isPrimaryLang ? el.Label : el.LabelLang2;

          // HelperText support for "Param.*"
          let text = isPrimaryLang ? el.HelperText : el.HelperTextLang2;
          if (text === null || text === undefined || text === '') return '';
          if (text.indexOf("{CUInfo.") !== -1) Object.entries(state.loanEnquiryConfig.CreditUnionInfo).forEach(p => text = text.replaceAll("{CUInfo.".concat(p[0], "}"), p[1]));
          if (text.indexOf("{Param.") !== -1) state.loanEnquiryConfig.Params.forEach(p => text = text.replaceAll("{Param.".concat(p.Name, "}"), p.Value));
          if (text.indexOf("{Setting.") !== -1) state.loanEnquiryConfig.ReferenceData.SystemSettings.forEach(p => text = text.replaceAll("{Setting.".concat(p.Name, "}"), p.Value));
          state.elsConfig.lstHelperText[el.Name] = text;
        });
      }
    }
  },

  updateLocalLoanEnquiryData(state, payload) {
    state.loanEnquiryData = Object.assign(state.loanEnquiryData, payload);
  },

  updateLoanEnquiryConfig(state, payload) {
    state.loanEnquiryConfig = Object.assign(state.loanEnquiryConfig, payload);
  },

  confirmPayment(state) {
    state.paymentConfirmed = true;
  },

  resetState (state) {
    // Merge rather than replace so we don't lose observers
    // https://github.com/vuejs/vuex/issues/1118
    Object.assign(state, getDefaultState())
  },
};

const actions = {

  async loadLoanEnquiryConfig({ commit }) {
    const loanEnqConfig = await service.getLoanEnquiryConfig();
    commit("loanEnquiryConfiguration", loanEnqConfig);
    return loanEnqConfig;
  },

  updateLocalLoanEnquiryData({ commit }, payload) {
    commit("updateLocalLoanEnquiryData", payload);
  },

  updateLoanEnquiryConfig({ commit }, payload) {
    commit("updateLoanEnquiryConfig", payload);
  },

  async saveLoanEnquiry({ commit }, payload) {
    commit("updateLocalLoanEnquiryData", payload);
    const resp = await service.saveLoanEnquiry(this.state.loanEnquiryData);
    if (resp.ReturnedErrorCode === constants.LOAN_ENQUIRY_SUCCESS) {
      commit("updateLoanEnquiryConfig", { DocPack: resp.MemAppDocumentPackDetailList }); // Store DocPack in state
    }
    return resp;
  },

  async checkEligibility({ commit }, payload) {
    commit("updateLocalLoanEnquiryData", payload);
    return await service.checkEligibility(payload);
  }, 

  async otpRequest({ commit }, payload) {
    commit("updateLocalLoanEnquiryData", payload);
    return await service.otpRequest(payload);
  }, 

  async otpValidate({ commit }, payload) {
    commit("updateLocalLoanEnquiryData", payload);
    const resp = await service.otpValidate(payload);
    if (resp.Response.StatusCode === constants.LOAN_ENQUIRY_SUCCESS) {
      commit("updateLocalLoanEnquiryData", { loanEnquiryToken: resp.Token.TokenId }); // Store Token.TokenId in state
    }
    return resp;
  }, 

  async requestOpen({ commit }, payload) {
    commit("updateLocalLoanEnquiryData", payload);
    return await service.requestOpen(payload);
  }, 

  async openEnquiry({ commit }, payload) {
    commit("updateLocalLoanEnquiryData", payload);
    const resp = await service.openEnquiry(payload);
    if (resp.Code === constants.LOAN_ENQUIRY_SUCCESS) {
      commit("updateLoanEnquiryConfig", { DocPack: resp.Data });
      commit("updateLocalLoanEnquiryData", { accessToken : resp.AccessToken });
    }
    return resp;
  },

  async enquiryDoc({ commit }, payload) {
    const body = {
      EnquiryKey: this.state.loanEnquiryData.enquiryKey,
      AccessToken: this.state.loanEnquiryData.accessToken,
      DocumentTypeID: payload.docPackInfo.DocumentTypeID,
      DocumentPackDetailID: payload.docPackInfo.DocumentPackDetailID,
      Docs: payload.docPackFiles
    };
    const resp = await service.enquiryDoc(body);
    if (resp.Code === constants.LOAN_ENQUIRY_SUCCESS) {
      commit("updateLoanEnquiryConfig", { DocPack: resp.Data });
    }
    return resp;
  },

  confirmPayment({ commit }) {
    commit("confirmPayment");
  },

  resetState ({ commit }) {
    commit('resetState')
  },
};

const getters = {
  referenceData: state => { return state.loanEnquiryConfig.ReferenceData; },
  elementsConfig: state => { return state.elsConfig; },
};

const vuexLocalStorage = new VuexPersist({
  key: 'vuex', // The key to store the state on in the storage provider.
  storage: window.localStorage, // or window.sessionStorage or localForage
  // Function that passes the state and returns the state with only the objects you want to store.
  // reducer: state => state,
  // Function that passes a mutation and lets you decide if it should update the state in localStorage.
  // filter: mutation => (true)
});

export default new Vuex.Store({
  state,
  mutations,
  actions,
  getters,
  modules: {},
  plugins: [vuexLocalStorage.plugin]
});